import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Camera, RefreshCw } from 'lucide-react';
import { useAuth } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Cameras = () => {
  const { user } = useAuth();
  const [cameras, setCameras] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snapshots, setSnapshots] = useState({});

  const fetchCameras = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/cameras/list`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      if (data.success) {
        setCameras(data.cameras || []);
      }
    } catch (error) {
      console.error('Failed to load cameras:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSnapshot = async (cameraId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/cameras/${cameraId}/snapshot`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      if (data.success) {
        setSnapshots(prev => ({ ...prev, [cameraId]: data.data }));
      }
    } catch (error) {
      console.error('Failed to get snapshot:', error);
    }
  };

  useEffect(() => {
    if (user) {
      fetchCameras();
    }
  }, [user]);

  // Redirect if not authorized
  if (!user || !['super_admin', 'admin'].includes(user.role)) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="p-6 text-center">
            <p className="text-gray-600">Camera access requires admin permissions</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-blue-50 to-slate-50 p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Security Cameras</h1>
          <p className="text-blue-800">UniFi Protect Integration</p>
        </div>
        <Button onClick={fetchCameras} disabled={loading} className="bg-blue-600 hover:bg-blue-700">
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {loading && cameras.length === 0 ? (
        <Card className="bg-white/80 backdrop-blur-xl">
          <CardContent className="p-12 text-center">
            <Camera className="h-16 w-16 text-slate-500 mx-auto mb-4 animate-pulse" />
            <p className="text-gray-600">Loading cameras...</p>
          </CardContent>
        </Card>
      ) : cameras.length === 0 ? (
        <Card className="bg-white/80 backdrop-blur-xl">
          <CardContent className="p-12 text-center">
            <Camera className="h-16 w-16 text-slate-500 mx-auto mb-4" />
            <p className="text-gray-600 mb-4">No cameras configured</p>
            <p className="text-sm text-gray-500">
              Configure UniFi Protect credentials in .env file:
              <br />
              UNIFI_HOST, UNIFI_USERNAME, UNIFI_PASSWORD
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {cameras.map(camera => (
            <Card key={camera.id} className="bg-white/80 backdrop-blur-xl border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg">{camera.name || 'Camera'}</CardTitle>
                <div className="flex gap-2 mt-2">
                  {camera.state === 'CONNECTED' && (
                    <span className="px-2 py-1 bg-green-500 text-white text-xs rounded-full">Online</span>
                  )}
                  {camera.isRecording && (
                    <span className="px-2 py-1 bg-red-500 text-white text-xs rounded-full">● Recording</span>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {snapshots[camera.id] ? (
                  <img 
                    src={snapshots[camera.id]} 
                    alt={camera.name}
                    className="w-full rounded-lg mb-3"
                  />
                ) : (
                  <div className="w-full h-40 bg-gray-200 rounded-lg mb-3 flex items-center justify-center">
                    <Camera className="h-12 w-12 text-slate-500" />
                  </div>
                )}
                <Button 
                  onClick={() => getSnapshot(camera.id)} 
                  className="w-full"
                  size="sm"
                >
                  Get Snapshot
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default Cameras;
